import { ProductTypes, type ProductType } from '../../types';
import { SubmissionFirmographics } from '../../api/submissions/submissions.api.types';
import {
  PRIME_ONE_TECH_DOMESTIC_REVENUE_CURRENT_FY_FIELD_ID,
  PRIME_ONE_TECH_DOMESTIC_REVENUE_LAST_FY_FIELD_ID,
  PRIME_ONE_TECH_DOMESTIC_REVENUE_NEXT_FY_FIELD_ID,
  PRIME_ONE_TECH_OTHER_TERRITORY_REVENUE_CURRENT_FY_FIELD_ID,
  PRIME_ONE_TECH_OTHER_TERRITORY_REVENUE_LAST_FY_FIELD_ID,
  PRIME_ONE_TECH_OTHER_TERRITORY_REVENUE_NEXT_FY_FIELD_ID,
  PRIME_ONE_TECH_TOTAL_REVENUE_CURRENT_FY_FIELD_ID,
  PRIME_ONE_TECH_TOTAL_REVENUE_LAST_FY_FIELD_ID,
  PRIME_ONE_TECH_TOTAL_REVENUE_NEXT_FY_FIELD_ID,
  PRIME_ONE_TECH_USA_REVENUE_CURRENT_FY_FIELD_ID,
  PRIME_ONE_TECH_USA_REVENUE_LAST_FY_FIELD_ID,
  PRIME_ONE_TECH_USA_REVENUE_NEXT_FY_FIELD_ID,
  PRIME_ONE_AU_TOTAL_REVENUE_FIELD_ID,
  PRIME_ONE_AU_NON_AUSTRALIA_REVENUE_FIELD_ID,
  PRIME_ONE_AU_NON_AUSTRALIA_EXCLUDING_USA_REVENUE_FIELD_ID,
  PRIME_ONE_AU_NON_AUSTRALIA_USA_REVENUE_FIELD_ID,
  P_ONE_AU_ABN_FIELD_ID,
} from '../constants';

/**
 * Watches specific fields based on the product type.
 * @param productType - The product type.
 * @param watch - The watch function from React Hook Form.
 * @returns An object containing the watched fields.
 */
export const watchFieldsBasedOnProductType = (
  productType: ProductType | undefined,
  watch: (fieldNames: string[]) => Record<string, any>
): Record<string, any> => {
  let fieldsToWatch: string[] = [];

  if (productType === ProductTypes.prime_one_tech) {
    fieldsToWatch = [
      PRIME_ONE_TECH_DOMESTIC_REVENUE_CURRENT_FY_FIELD_ID,
      PRIME_ONE_TECH_DOMESTIC_REVENUE_LAST_FY_FIELD_ID,
      PRIME_ONE_TECH_DOMESTIC_REVENUE_NEXT_FY_FIELD_ID,
      PRIME_ONE_TECH_USA_REVENUE_CURRENT_FY_FIELD_ID,
      PRIME_ONE_TECH_USA_REVENUE_LAST_FY_FIELD_ID,
      PRIME_ONE_TECH_USA_REVENUE_NEXT_FY_FIELD_ID,
      PRIME_ONE_TECH_OTHER_TERRITORY_REVENUE_CURRENT_FY_FIELD_ID,
      PRIME_ONE_TECH_OTHER_TERRITORY_REVENUE_LAST_FY_FIELD_ID,
      PRIME_ONE_TECH_OTHER_TERRITORY_REVENUE_NEXT_FY_FIELD_ID,
    ];
  }

  if (productType === ProductTypes.prime_one_au) {
    fieldsToWatch = [
      PRIME_ONE_AU_NON_AUSTRALIA_EXCLUDING_USA_REVENUE_FIELD_ID,
      PRIME_ONE_AU_NON_AUSTRALIA_USA_REVENUE_FIELD_ID,
    ];
  }
  // Get the array of watched values
  const watchedValues = watch(fieldsToWatch);

  return fieldsToWatch.reduce((acc, fieldId, index) => {
    acc[fieldId] = watchedValues[index]?.value; // Map fieldId to its corresponding value
    return acc;
  }, {} as Record<string, any>);
};
/**
 * Parses a Currency value to a number. Handles strings with currency symbols.
 * @param value - The value to parse.
 * @returns The parsed number or 0 if invalid.
 */
export const parseCurrencyValue = (value: any): number => {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    return value === '£' || value === '$' || value === ''
      ? 0
      : parseFloat(value.replace(/[£$,]/g, ''));
  }
  return 0;
};

/**
 * Calculates and sets the total revenue for a specific fiscal year.
 * @param watchFields - The fields being watched in the form.
 * @param setValue - The method to set the value in the form.
 * @param fieldIds - The field IDs for domestic, USA, and other territory revenues.
 * @param totalRevenueFieldId - The field ID for the total revenue.
 */
const calculateAndSetTotalRevenue = (
  watchFields: Record<string, any>,
  setValue: (field: string, value: any) => void,
  fieldIds: {
    domestic: string;
    usa: string;
    otherTerritory: string;
  },
  totalRevenueFieldId: string
) => {
  if (
    watchFields[fieldIds.domestic] != undefined &&
    watchFields[fieldIds.usa] != undefined &&
    watchFields[fieldIds.otherTerritory] != undefined
  ) {
    const domesticRevenue = parseCurrencyValue(watchFields[fieldIds.domestic]);
    const usaRevenue = parseCurrencyValue(watchFields[fieldIds.usa]);
    const otherTerritoryRevenue = parseCurrencyValue(
      watchFields[fieldIds.otherTerritory]
    );

    const totalRevenue = {
      value: `£${domesticRevenue + usaRevenue + otherTerritoryRevenue}`,
    };

    setValue(totalRevenueFieldId, totalRevenue);
  }
};

/**
 * Calculates and sets dynamically calculated fields based on product.
 * @param productType - The product type.
 * @param watchFields - The fields being watched in the form.
 * @param setValue - The method to set the value in the form.
 */
export const updateFormFieldsForProduct = (
  productType: ProductType | undefined,
  watchFields: Record<string, any>,
  setValue: (field: string, value: any) => void
) => {
  if (productType === ProductTypes.prime_one_tech) {
    // Calculate and set total revenue for Current FY
    calculateAndSetTotalRevenue(
      watchFields,
      setValue,
      {
        domestic: PRIME_ONE_TECH_DOMESTIC_REVENUE_CURRENT_FY_FIELD_ID,
        usa: PRIME_ONE_TECH_USA_REVENUE_CURRENT_FY_FIELD_ID,
        otherTerritory:
          PRIME_ONE_TECH_OTHER_TERRITORY_REVENUE_CURRENT_FY_FIELD_ID,
      },
      PRIME_ONE_TECH_TOTAL_REVENUE_CURRENT_FY_FIELD_ID
    );

    // Calculate and set total revenue for Last FY
    calculateAndSetTotalRevenue(
      watchFields,
      setValue,
      {
        domestic: PRIME_ONE_TECH_DOMESTIC_REVENUE_LAST_FY_FIELD_ID,
        usa: PRIME_ONE_TECH_USA_REVENUE_LAST_FY_FIELD_ID,
        otherTerritory: PRIME_ONE_TECH_OTHER_TERRITORY_REVENUE_LAST_FY_FIELD_ID,
      },
      PRIME_ONE_TECH_TOTAL_REVENUE_LAST_FY_FIELD_ID
    );

    // Calculate and set total revenue for Next FY
    calculateAndSetTotalRevenue(
      watchFields,
      setValue,
      {
        domestic: PRIME_ONE_TECH_DOMESTIC_REVENUE_NEXT_FY_FIELD_ID,
        usa: PRIME_ONE_TECH_USA_REVENUE_NEXT_FY_FIELD_ID,
        otherTerritory: PRIME_ONE_TECH_OTHER_TERRITORY_REVENUE_NEXT_FY_FIELD_ID,
      },
      PRIME_ONE_TECH_TOTAL_REVENUE_NEXT_FY_FIELD_ID
    );
  }

  if (productType === ProductTypes.prime_one_au) {
    const totalIntRevenue =
      parseCurrencyValue(
        watchFields[PRIME_ONE_AU_NON_AUSTRALIA_EXCLUDING_USA_REVENUE_FIELD_ID]
      ) +
      parseCurrencyValue(
        watchFields[PRIME_ONE_AU_NON_AUSTRALIA_USA_REVENUE_FIELD_ID]
      );
    setValue(PRIME_ONE_AU_NON_AUSTRALIA_REVENUE_FIELD_ID, {
      value: totalIntRevenue,
    });
  }
};

/**
 * Calculates and sets dynamically calculated fields based on product.
 * @param payload - The entire payload.
 * @param dirtyFields - The fields changed by the user.
 */
export const selectDirtyFieldsFromPayload = (
  payload: any,
  dirtyFields: Record<string, boolean>
) => {
  const selectedFields = Object.keys(dirtyFields).reduce((prev, field) => {
    if (dirtyFields[field]) {
      prev[field] = payload[field];
    }

    return prev;
  }, {} as Record<string, any>);

  return selectedFields;
};

/**
 * Generates default values for security questions form fields based on product type and firmographics data.
 *
 * @param productType - The product type to generate default values for
 * @param firmographics - The firmographics data from the submission
 * @returns Object containing field ID to default value mappings
 */
export const getDefaultSecurityQuestionsFormValues = (
  productType: ProductType,
  firmographics: SubmissionFirmographics
): Record<string, { value: unknown }> => {
  const defaultValues: Record<string, { value: unknown }> = {};

  if (productType === ProductTypes.prime_one_au) {
    if (
      firmographics.totalRevenue !== undefined &&
      firmographics.totalRevenue !== null
    ) {
      defaultValues[PRIME_ONE_AU_TOTAL_REVENUE_FIELD_ID] = {
        value: firmographics.totalRevenue,
      };
    }

    if (
      firmographics.foreignRevenue !== undefined &&
      firmographics.foreignRevenue !== null
    ) {
      defaultValues[PRIME_ONE_AU_NON_AUSTRALIA_REVENUE_FIELD_ID] = {
        value: firmographics.foreignRevenue,
      };
    }

    if (firmographics.ein !== undefined && firmographics.ein !== null) {
      defaultValues[P_ONE_AU_ABN_FIELD_ID] = {
        value: firmographics.ein,
      };
    }
  }

  return defaultValues;
};
